import { Component, EventEmitter, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { QuestionViewComponent } from './question-view/question-view.component';
import { NotificationService } from '@progress/kendo-angular-notification';
import { FileManagementService } from '@app/shared/services/file-management.service';
import { AnswerAttachment, QuestionCollectionResponse } from '@app/dashboard/shared/models/audit-collection-response';
import { AuditProgress } from '@app/dashboard/shared/models/audit-progress';
import { InsertAnswers } from '@app/dashboard/shared/models/insert-answer';
import { mapQuestionCollectionToInsertAnswers } from '@app/dashboard/shared/functions/audit-mapper';
import { StringConstants } from '@app/shared/string-constants';

@Component({
	selector: 'kpa-audit',
	templateUrl: './audit.component.html',
	styleUrl: './audit.component.scss',
})
export class AuditComponent {
	@Input() dialogOpened: boolean = false;
	@Output() closeDialog = new EventEmitter();
	@Input() progress: AuditProgress[];
	@Input() questionCollection: QuestionCollectionResponse;
	@Input() defaultQuestionPerPage: number;
	@Input() scheduledAuditId: number;
	@Input() perPageQuestionIds: number[];
	@Input() currentQuestionNumber: number;

	@Output() paginationChange: EventEmitter<any> = new EventEmitter<any>();
	@Output() onItemPerPageChange: EventEmitter<any> = new EventEmitter<any>();
	@Output() submitAudit: EventEmitter<any> = new EventEmitter<any>();
	@ViewChildren(QuestionViewComponent) questionComponents: QueryList<QuestionViewComponent>;

	addToAuditFiles: Array<{ file: File; questionId: number }> = [];
	deleteAuditFiles: AnswerAttachment[] = [];
	isValidResponse: boolean = false;
	isItemChangeValid: boolean | null = null;
	constructor(
		private notificationService: NotificationService,
		private fileManagementService: FileManagementService
	) {}

	onFileAdded(event: { file: File; questionId: number }) {
		this.addToAuditFiles = [];
		this.addToAuditFiles.push(event);
	}

	onFileRemoved(attachment: AnswerAttachment) {
		this.deleteAuditFiles.push(attachment);
	}

	public open(component: string): void {
		this[component + 'Opened'] = true;
	}

	public close(component: string): void {
		this[component + 'Opened'] = false;
		this.closeDialog.emit();
	}
	async updateItemsPerPage(itemCount: number) {
		const invalidQuestions = this.validateCurrentQuestions();

		if (invalidQuestions.length === 0) {
			let answers = this.mapToInsertAnswers();
			const questionWithAttachment = this.addToAuditFiles.find((x) => answers.answers.some((a) => a.questionId === x.questionId));
			if (questionWithAttachment) {
				const file = questionWithAttachment.file;
				const attachmentId = await this.uploadAuditFile(file);
				const answer = answers.answers.find((x) => x.questionId === questionWithAttachment.questionId);

				if (answer) {
					if (!answer.fileExternalIds) {
						answer.fileExternalIds = [];
					}
					answer.fileExternalIds.push(attachmentId);
				}
			}

			const request = {
				answers: answers,
				itemsPerPage: itemCount,
			};
			this.isItemChangeValid = true;
			this.onItemPerPageChange.emit(request);
		} else {
			invalidQuestions.forEach((q) => {
				this.notificationService.show({
					content: `Please provide a valid response for question #${q.questionData.orderNumber}.`,
					hideAfter: StringConstants.hideAuditNotificationAfter,
					position: { horizontal: 'right', vertical: 'bottom' },
					animation: { type: 'fade', duration: 400 },
					type: { style: 'error', icon: true },
				});
			});
			this.isItemChangeValid = false;
		}
	}
	validateCurrentQuestions(): QuestionViewComponent[] {
		const currentQuestions = this.questionComponents.filter((component) =>
			this.perPageQuestionIds.includes(component.questionData.orderNumber)
		);
		return currentQuestions.filter((q) => !q.validateResponse(q.questionData));
	}

	async uploadAuditFile(file: File): Promise<string> {
		const fileUploadFormData = new FormData();
		fileUploadFormData.append('file', file, file.name);
		return new Promise((resolve, reject) => {
			this.fileManagementService.uploadAuditFile(fileUploadFormData).subscribe({
				next: (response) => {
					resolve(response); // Assuming response contains attachmentId
				},
				error: (err) => {
					this.notificationService.show({
						content: 'Error while uploading document.',
						hideAfter: StringConstants.hideAuditNotificationAfter,
						position: { horizontal: 'right', vertical: 'bottom' },
						animation: { type: 'fade', duration: 400 },
						type: { style: 'error', icon: true },
					});
					reject(err);
				},
			});
		});
	}

	async onValidateRequest(upcomingQuestion: number) {
		const invalidQuestions = this.validateCurrentQuestions();
		if (invalidQuestions.length === 0) {
			let answers = this.mapToInsertAnswers();
			const questionWithAttachment = this.addToAuditFiles.find((x) => answers.answers.some((a) => a.questionId === x.questionId));
			if (questionWithAttachment) {
				const file = questionWithAttachment.file;
				const attachmentId = await this.uploadAuditFile(file);
				const answer = answers.answers.find((x) => x.questionId === questionWithAttachment.questionId);

				if (answer) {
					if (!answer.fileExternalIds) {
						answer.fileExternalIds = [];
					}
					answer.fileExternalIds.push(attachmentId);
				}
			}

			const request = {
				currentQuestionNumber: this.currentQuestionNumber,
				nextQuestionNumber: upcomingQuestion,
				answers: answers,
			};
			this.paginationChange.emit(request);
		} else {
			invalidQuestions.forEach((q) => {
				this.notificationService.show({
					content: `Please provide a valid response for question #${q.questionData.orderNumber}.`,
					hideAfter: StringConstants.hideAuditNotificationAfter,
					position: { horizontal: 'right', vertical: 'bottom' },
					animation: { type: 'fade', duration: 400 },
					type: { style: 'error', icon: true },
				});
			});
		}
	}

	public mapToInsertAnswers(): InsertAnswers {
		const currentQuestions = this.questionComponents
			.filter((component) => this.perPageQuestionIds.includes(component.questionData.orderNumber))
			.map((component) => component.questionData);

		let answerCollection: QuestionCollectionResponse = {
			scheduledAuditId: this.scheduledAuditId,
			questions: currentQuestions,
		};

		return mapQuestionCollectionToInsertAnswers(answerCollection);
	}

	onSubmit(upcomingQuestion: number) {
		const invalidQuestions = this.validateCurrentQuestions();

		if (invalidQuestions.length === 0) {
			const answers = this.mapToInsertAnswers();
			const request = {
				currentQuestionNumber: this.currentQuestionNumber,
				nextQuestionNumber: upcomingQuestion,
				answers: answers,
			};
			this.submitAudit.emit(request);
		} else {
			invalidQuestions.forEach((q) => {
				this.notificationService.show({
					content: `Please provide a valid response for question #${q.questionData.orderNumber}.`,
					hideAfter: StringConstants.hideAuditNotificationAfter,
					position: { horizontal: 'right', vertical: 'bottom' },
					animation: { type: 'fade', duration: 400 },
					type: { style: 'error', icon: true },
				});
			});
		}
	}
}
